import { create } from 'zustand';
import type { BrandingConfigDTO } from '@features/settings/application/dto/BrandingConfigDTO';

interface BrandingState {
  branding: BrandingConfigDTO | null;
  isLoaded: boolean;

  // Actions
  setBranding: (branding: BrandingConfigDTO) => void;
  clearBranding: () => void;
}

/**
 * Global branding store
 * Manages workspace branding configuration and applies it to the document
 */
export const useBrandingStore = create<BrandingState>((set) => ({
  branding: null,
  isLoaded: false,

  setBranding: (branding) => {
    set({ branding, isLoaded: true });
    // Apply CSS variables globally
    applyBrandingToDocument(branding);
  },

  clearBranding: () => {
    set({ branding: null, isLoaded: false });
    removeBrandingFromDocument();
  },
}));

// Helper functions for CSS variable injection

/**
 * Apply branding configuration to document as CSS variables
 */
function applyBrandingToDocument(branding: BrandingConfigDTO) {
  const root = document.documentElement;

  // Colors
  root.style.setProperty('--brand-primary', branding.primaryColor);
  root.style.setProperty('--brand-secondary', branding.secondaryColor);
  root.style.setProperty('--brand-text', branding.textColor);
  root.style.setProperty('--brand-background', branding.backgroundColor);

  // Typography
  root.style.setProperty('--brand-font-family', getFontFamilyCSS(branding.fontFamily));
  root.style.setProperty('--brand-font-size', `${branding.fontSize}px`);
  root.style.setProperty('--brand-font-weight', branding.fontWeight.toString());
  root.style.setProperty('--brand-line-height', branding.lineHeight.toString());
  root.style.setProperty('--brand-letter-spacing', `${branding.letterSpacing}px`);

  // Layout
  root.style.setProperty('--brand-border-radius', `${branding.borderRadius}px`);

  // Generate color variants (hover, light)
  root.style.setProperty('--brand-primary-hover', adjustColor(branding.primaryColor, -10));

  // Generate light variant with proper contrast text
  const primaryLight = generateLightVariant(branding.primaryColor);
  root.style.setProperty('--brand-primary-light', primaryLight);
  root.style.setProperty('--brand-primary-light-text', getContrastingTextColor(primaryLight));

  root.style.setProperty('--brand-secondary-hover', adjustColor(branding.secondaryColor, -10));

  // Set contrasting text colors for primary and secondary
  root.style.setProperty('--brand-primary-text', getContrastingTextColor(branding.primaryColor));
  root.style.setProperty('--brand-secondary-text', getContrastingTextColor(branding.secondaryColor));

  // Set data-preset attribute for theme-specific CSS
  root.setAttribute('data-preset', branding.presetId || 'default');
}

/**
 * Remove branding CSS variables from document
 */
function removeBrandingFromDocument() {
  const root = document.documentElement;
  const vars = [
    '--brand-primary',
    '--brand-secondary',
    '--brand-text',
    '--brand-background',
    '--brand-font-family',
    '--brand-font-size',
    '--brand-font-weight',
    '--brand-line-height',
    '--brand-letter-spacing',
    '--brand-border-radius',
    '--brand-primary-hover',
    '--brand-primary-light',
    '--brand-primary-light-text',
    '--brand-primary-text',
    '--brand-secondary-hover',
    '--brand-secondary-text',
  ];
  vars.forEach((v) => root.style.removeProperty(v));

  // Remove data-preset attribute
  root.removeAttribute('data-preset');
}

/**
 * Get CSS font family string from font family identifier
 */
function getFontFamilyCSS(fontFamily: string): string {
  const map: Record<string, string> = {
    inter: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
    georgia: 'Georgia, "Times New Roman", serif',
    monaco: 'Monaco, "Courier New", monospace',
  };
  return map[fontFamily] || map.inter;
}

/**
 * Adjust color brightness by a percentage
 * @param hex - Hex color string (e.g., '#6D5DFE')
 * @param percent - Percentage to adjust (-100 to 100)
 */
function adjustColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + percent));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + percent));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + percent));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/**
 * Generate a light variant of a color (for backgrounds)
 * Always produces a light, pastel-like color suitable for backgrounds
 */
function generateLightVariant(hex: string): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;

  // Mix with white to create a light tint (90% white, 10% color)
  const lightR = Math.round(r * 0.15 + 255 * 0.85);
  const lightG = Math.round(g * 0.15 + 255 * 0.85);
  const lightB = Math.round(b * 0.15 + 255 * 0.85);

  return `#${((lightR << 16) | (lightG << 8) | lightB).toString(16).padStart(6, '0')}`;
}

/**
 * Calculate relative luminance of a color
 * Based on WCAG 2.1 formula
 */
function getLuminance(hex: string): number {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = ((num >> 16) & 0xff) / 255;
  const g = ((num >> 8) & 0xff) / 255;
  const b = (num & 0xff) / 255;

  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Get contrasting text color (black or white) based on background luminance
 * Returns dark text for light backgrounds, light text for dark backgrounds
 */
function getContrastingTextColor(backgroundColor: string): string {
  const luminance = getLuminance(backgroundColor);
  // Use 0.5 as threshold (WCAG recommends different thresholds but 0.5 works well visually)
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
}

// Selectors
export const brandingSelectors = {
  selectBranding: (state: BrandingState) => state.branding,
  selectIsLoaded: (state: BrandingState) => state.isLoaded,
  selectPrimaryColor: (state: BrandingState) => state.branding?.primaryColor ?? '#6D5DFE',
  selectSecondaryColor: (state: BrandingState) => state.branding?.secondaryColor ?? '#10B981',
  selectCompanyName: (state: BrandingState) => state.branding?.companyName ?? 'Coursue',
  selectLogoLightUrl: (state: BrandingState) => state.branding?.logoLightUrl,
  selectLogoDarkUrl: (state: BrandingState) => state.branding?.logoDarkUrl,
  selectFaviconUrl: (state: BrandingState) => state.branding?.faviconUrl,
};
